import { useState } from "react";
import { Column } from "../../../../core/components/Column";
import { TextComponent } from "../../../../core/components/TextComponent";
import { CustomButton } from "../../../../core/components/CustomButton";
import { Field, FieldProps, Form, Formik, ErrorMessage, FormikHelpers } from "formik";
import { onSendNewTicket } from "../../../profile/presentation/store/OnboardingThunk";
import { AppDispatch } from "../../../../core/store/store";
import { useDispatch } from "react-redux";
import { TicketFormValues, ticketValidationSchema } from "../interface/TicketFormInterface";
import { ImageUploader } from "../../../../core/components/ImageUploader";
import { useNavigate } from "react-router-dom";
import { ArrowDown2 } from "../../../../core/icons/ArrowDown";

const ISSUE_CATEGORIES = [
	"Login and access",
	"Account setup and profile",
	"Username or profile photo",
	"Photo upload",
	"Verification request",
	"Matches not loading",
	"Messages or chat issues",
	"Notifications not received",
	"App crashes or freezes",
	"Slow performance",
	"Payments and billing",
	"Subscription issues",
	"Report or block user",
	"Safety concern",
	"Bug in settings",
	"Search and filters",
	"Location issue",
	"Audio or video issue",
	"Feature request",
	"Other",
];

export const ReportIssueScreen: React.FC = () => {
	const dispatch: AppDispatch = useDispatch();
	const navigate = useNavigate();
	const [file, setFile] = useState<File | null>(null);
	const [isSubmitted, setIsSubmitted] = useState(false);

	const initialValues: TicketFormValues = {
		issueCategory: "",
		issueDescription: "",
	};

	const handleNewTicket = async (
		values: TicketFormValues,
		{ setSubmitting, resetForm }: FormikHelpers<TicketFormValues>,
	) => {
		const cleanCategory = values.issueCategory.trim();
		const cleanDescription = values.issueDescription.trim();
		const issueBody = `Issue type: ${cleanCategory}\n\n${cleanDescription}`;

		await dispatch(
			onSendNewTicket({
				type: "ticket",
				title: "Issue Report",
				issue: issueBody,
				...(file && { file: file }),
			}),
		);

		setSubmitting(false);
		setIsSubmitted(true);
		resetForm();
		setFile(null);
	};

	return (
		<div className="bg-white max-md:overflow-y-auto">
			<Column className="p-4 sm:w-full lg:w-3/4 xl:w-2/4 gap-4">
				<div className="flex items-center gap-3">
					<button
						type="button"
						className="w-10 aspect-square flex items-center justify-center text-primary transform rotate-90 rounded-full hover:bg-grayDefault"
						onClick={() => navigate("/settings")}
						aria-label="Back to settings"
					>
						<ArrowDown2 />
					</button>
					<TextComponent variant="h4">Report an Issue</TextComponent>
				</div>

				{isSubmitted && (
					<div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700 text-sm">
						Your report has been sent successfully.
					</div>
				)}

				<Formik
					initialValues={initialValues}
					validationSchema={ticketValidationSchema}
					onSubmit={handleNewTicket}
				>
					{({ isSubmitting }) => (
						<Form className="flex flex-col gap-4 rounded-lg bg-socondary p-4">
							<Column className="gap-1">
								<TextComponent variant="h6">Issue Type</TextComponent>
								<div className="relative">
									<Field
										as="select"
										id="issueCategory"
										name="issueCategory"
										className="appearance-none outline-none text-sm border border-[#E6E6E6] rounded-lg py-3 pl-3 pr-12 w-full bg-white"
									>
										<option value="" disabled>
											Select the issue that best matches your problem
										</option>
										{ISSUE_CATEGORIES.map((category) => (
											<option key={category} value={category}>
												{category}
											</option>
										))}
									</Field>
									<span className="pointer-events-none absolute right-6 top-1/2 -translate-y-1/2 text-gray-700">
										<ArrowDown2 />
									</span>
								</div>
								<ErrorMessage
									name="issueCategory"
									component="div"
									className="text-red-500 text-xs"
								/>
							</Column>

							<Column className="gap-1">
								<TextComponent variant="h6">Issue Details</TextComponent>
								<Field name="issueDescription">
									{({ field }: FieldProps<string>) => (
										<textarea
											{...field}
											id="issueDescription"
											placeholder="Please describe what happened and what you expected to happen."
											rows={4}
											onInput={(event) => {
												const target = event.currentTarget;
												target.style.height = "auto";
												target.style.height = `${target.scrollHeight}px`;
											}}
											className="w-full text-sm resize-none rounded-lg border px-4 py-4 placeholder:text-grayLight2 placeholder:select-none outline-none overflow-hidden"
										/>
									)}
								</Field>
								<ErrorMessage
									name="issueDescription"
									component="div"
									className="text-red-500 text-xs"
								/>
								<TextComponent variant="h8" className="text-xs text-grayLight2">
									This report is sent with the title: Issue Report.
								</TextComponent>
							</Column>

							<div className="relative">
								{file && (
									<CustomButton
										className="absolute top-2 right-2 z-10 text-grayLight"
										onClick={() => setFile(null)}
										aria-label="Remove Image"
										type="button"
										small
									>
										Delete
									</CustomButton>
								)}
								<ImageUploader
									className="w-full h-44"
									inputId="fileInput"
									file={file}
									setFile={setFile}
								/>
							</div>

							<CustomButton type="submit" loading={isSubmitting}>
								Send Report
							</CustomButton>
						</Form>
					)}
				</Formik>
			</Column>
		</div>
	);
};
